import { useEffect, useState } from 'react';
import { FieldValues, useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import { useSearchParams } from 'react-router-dom';
import { Cell } from 'react-table';

import { Permissions } from 'types';

import { useAppDispatch, useAppSelector } from 'redux/hooks';
import {
  getCurrentPage,
  getSize,
  setTableCurrentPage,
  setTablePagination,
} from 'redux/slices/tableConfig';
import { setUsers, usersSelector } from 'redux/slices/users';

import { usersDataService } from 'services/users.service';

import { useFetch } from 'hooks/useFetch';

import { UserRow, pickUserColumns } from './columns.data';
import { createFilters } from './filters.data';

const useList = () => {
  const dispatch = useAppDispatch();

  const users = useAppSelector(usersSelector.selectAll);
  const page = useAppSelector(getCurrentPage);
  const size = useAppSelector(getSize);
  const userTypes = useAppSelector((state) => state?.constants?.userTypes);

  const filters = createFilters(userTypes);

  const [params, paramsSet] = useSearchParams({});

  const [isUpdatingPermissions, isUpdatingPermissionsSet] = useState<boolean>(false);

  const { register, handleSubmit, control, reset, setValue } = useForm();

  const { isLoading, data } = useFetch(usersDataService.getAll(page, size, params), [
    page,
    size,
    params,
    isUpdatingPermissions,
  ]);

  const modifyUserPermissions = async (cell: Cell) => {
    const userId = cell.row.original.id;
    const permissionName = cell.column.id.toUpperCase();
    const currentValue = cell.value;

    isUpdatingPermissionsSet(true);

    try {
      const res = await usersDataService.getUser(userId);

      let newPermissions = [...res.data.permissions];

      if (!currentValue) {
        // @ts-ignore
        newPermissions = [...newPermissions, permissionName];
      } else {
        newPermissions = [...newPermissions].filter(
          (permission) => permission !== permissionName
        );
      }

      await usersDataService.putUser(userId, { ...res.data, permissions: newPermissions });
    } catch (e) {
      toast.error('Permission update failed!');
    }

    isUpdatingPermissionsSet(false);
  };

  const userColumns = pickUserColumns(modifyUserPermissions, isUpdatingPermissions);

  const onSubmit = (filtersData: FieldValues) => {
    Object.keys(filtersData).forEach((key) => {
      if (filtersData[key] === undefined || filtersData[key] === '') {
        delete filtersData[key];
      }
    });

    paramsSet(filtersData);
  };

  const usersDataMapped = users?.map(
    (item): UserRow => ({
      email: item?.email,
      name: item?.name,
      last_name: item?.surname,
      view_price: item?.permissions && item.permissions.includes(Permissions.VIEW_PRICE),
      view_distributor_price:
        item?.permissions && item.permissions.includes(Permissions.VIEW_DISTRIBUTOR_PRICE),
      view_inactive_items:
        item?.permissions && item.permissions.includes(Permissions.VIEW_INACTIVE_ITEMS),
      insert_order: item?.permissions && item.permissions.includes(Permissions.INSERT_ORDER),
      view_order: item?.permissions && item.permissions.includes(Permissions.VIEW_ORDER),
      distributor: item?.permissions && item.permissions.includes(Permissions.DISTRIBUTOR),
      insert_budget: item?.permissions && item.permissions.includes(Permissions.INSERT_BUDGET),
      id: item.id,
    })
  );

  useEffect(() => {
    if (data) {
      const { totalElements, empty } = data;

      if (totalElements > 0 && empty) {
        dispatch(setTableCurrentPage(0));
      } else {
        dispatch(setUsers(data.content));
        dispatch(setTablePagination({ totalPages: data.totalPages }));
      }
    }
  }, [data]);

  return {
    userColumns,
    usersDataMapped,
    isLoading,
    filters,
    register,
    handleSubmit,
    reset,
    control,
    onSubmit,
    setValue,
    params,
  };
};

export { useList };
